import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { buildFeedbackContext } from '../../data-access/feedback-context.util';
import { FeedbackDialogComponent } from '../feedback-dialog/feedback-dialog.component';

/**
 * Floating "Feedback" launcher shown on every authenticated page, mirroring
 * `TalkToUsWidgetComponent`. Hides itself while already on `/feedback`, and computes the
 * current page's human-readable context on open to show as a transparency chip in the dialog.
 */
@Component({
    selector: 'app-feedback-widget',
    standalone: true,
    imports: [CommonModule, TranslateModule, ButtonComponent, FeedbackDialogComponent],
    templateUrl: './feedback-widget.component.html'
})
export class FeedbackWidgetComponent {
    public readonly showDialog = signal<boolean>(false);
    public readonly contextLabel = signal<string | null>(null);

    private readonly _router = inject(Router);
    private readonly _activatedRoute = inject(ActivatedRoute);

    private readonly _currentUrl = toSignal(
        this._router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => event.urlAfterRedirects)
        ),
        { initialValue: this._router.url }
    );

    public readonly showLauncher = computed(() => !this._currentUrl().split('?')[0].startsWith('/feedback'));

    public open(): void {
        const context = buildFeedbackContext(this._router, this._activatedRoute);
        this.contextLabel.set(context.pageTitle || null);
        this.showDialog.set(true);
    }
}
